import React from "react";
import { motion } from "framer-motion";
import { GlassCard } from "@/components/ui-kit";
import { Crown, Trophy } from "lucide-react";

const MEDAL = ["#FFD700", "#C0C0C0", "#CD7F32"];

export default function SchoolsLeaderboard({ schools = [] }) {
  const ranked = [...schools]
    .map((s) => ({
      id: s.id,
      name: s.name,
      value: s.stats?.executed_opportunities || 0,
      hours: s.stats?.executed_hours || 0,
    }))
    .sort((a, b) => b.value - a.value || b.hours - a.hours);
  const top = ranked[0]?.value || 0;
  const podium = ranked.slice(0, 3);
  const rest = ranked.slice(3);

  return (
    <GlassCard data-testid="schools-leaderboard">
      <div className="flex items-center gap-2 mb-1">
        <Trophy className="text-amber-500" size={22} />
        <h3 className="font-head font-bold text-lg text-stone-800">لوحة صدارة المدارس — بالفرص المنفّذة</h3>
      </div>
      <p className="text-xs text-stone-500 mb-5">التنافس الحقيقي يُحتسب من عدد الفرص التطوعية المنفّذة داخل كل مدرسة 🔥</p>

      {!ranked.length ? (
        <p className="text-stone-500 text-sm py-4 text-center">لا توجد بيانات بعد</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-6 items-end">
            {[1, 0, 2].map((pos) => {
              const s = podium[pos];
              if (!s) return <div key={pos} />;
              const isFirst = pos === 0;
              return (
                <motion.div
                  key={s.id}
                  data-testid={`lb-podium-${pos}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: pos * 0.08 }}
                  className={`relative rounded-2xl p-4 text-center border ${
                    isFirst
                      ? "bg-gradient-to-b from-amber-100 to-white border-amber-400 shadow-lg"
                      : "bg-white/70 border-amber-300/50"
                  }`}
                >
                  <div className="mx-auto w-11 h-11 rounded-full flex items-center justify-center mb-2"
                    style={{ background: MEDAL[pos], color: "#1A120B" }}>
                    {isFirst ? <Crown size={22} /> : <span className="font-bold">{pos + 1}</span>}
                  </div>
                  <div className="text-sm font-bold text-stone-800 truncate" title={s.name}>{s.name}</div>
                  <div className="text-lg font-head font-bold text-amber-600 mt-1">{s.value}<span className="text-xs"> فرصة منفّذة</span></div>
                  <div className="text-[11px] text-stone-500">{s.hours} ساعة</div>
                </motion.div>
              );
            })}
          </div>

          {rest.length > 0 && (
            <div className="space-y-2 max-h-80 overflow-y-auto pe-1">
              {rest.map((s, i) => (
                <div key={s.id} data-testid={`rank-row-${i + 4}`} className="flex items-center gap-3 py-2 px-3 rounded-xl hover:bg-amber-100/60 transition">
                  <div className="w-7 h-7 rounded-full bg-amber-500/15 text-amber-700 text-xs font-bold flex items-center justify-center shrink-0">{i + 4}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-stone-800 font-medium truncate">{s.name}</div>
                    <div className="h-1.5 rounded-full bg-stone-200/70 mt-1 overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-l from-amber-400 to-sky-400" style={{ width: `${top ? (s.value / top) * 100 : 0}%` }} />
                    </div>
                  </div>
                  <div className="text-left shrink-0">
                    <div className="text-sm font-bold text-amber-600">{s.value} فرصة</div>
                    <div className="text-[10px] text-stone-400">{s.hours} ساعة</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </GlassCard>
  );
}
